import React, { useState, useRef, useEffect } from "react";
import makeCloudinaryUrlForPhoto from '../../react/makeCloudinaryUrlForPhoto'
import './MenuCategoriesPreview.scss'

// CategoryGrid (ES6 React, no Tailwind)
// Anti-flash image loading:
// - Keep showing the previous image while the next one preloads.
// - Only swap the <img src> after the new image has finished loading.
// - If the next product has no photo (or load fails), fall back without flashing.

const cn = (...xs) => xs.filter(Boolean).join(" ");

function CategoryTile({ cat }) {
  // Sort products: those with photos first (non-mutating)
  const products = (Array.isArray(cat?.products) ? cat.products.slice() : []).sort((a, b) => {
    const aHas = !!a?.photo;
    const bHas = !!b?.photo;
    return aHas === bHas ? 0 : aHas ? -1 : 1;
  });

  const [idx, setIdx] = useState(0);
  const [displayedUrl, setDisplayedUrl] = useState(null); // the image currently shown
  const [isLoadingNext, setIsLoadingNext] = useState(false); // whether we're preloading the upcoming image
  const requestIdRef = useRef(0); // guards against race conditions when clicking quickly
  const touchStartX = useRef(null);

  const count = products.length;
  const title = cat?.name?.en || "Untitled";

  const buildUrl = (photo) => makeCloudinaryUrlForPhoto(photo, { width: 600 });

  // Initialize displayed image on mount
  useEffect(() => {
    const first = products[0];
    if (first?.photo) {
      const url = buildUrl(first.photo);
      // Preload first image then show
      const img = new Image();
      img.src = url;
      img.onload = () => setDisplayedUrl(url);
      img.onerror = () => setDisplayedUrl(null);
    } else {
      setDisplayedUrl(null);
    }
    
  }, []);

  // When idx changes, preload the next image but keep current visible until ready
  useEffect(() => {
    const current = products[idx];
    const rid = ++requestIdRef.current;

    if (current?.photo) {
      setIsLoadingNext(true);
      const url = buildUrl(current.photo);
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (requestIdRef.current === rid) {
          setDisplayedUrl(url);
          setIsLoadingNext(false);
        }
      };
      img.onerror = () => {
        if (requestIdRef.current === rid) {
          setIsLoadingNext(false);
          // Do not change displayedUrl; keep previous image to avoid flash
        }
      };
    } else {
      // No photo for the new item: remove image and show fallback
      setIsLoadingNext(false);
      setDisplayedUrl(null);
    }
  }, [idx, products]);

  const go = (dir) => {
    if (!count) return;
    setIdx((i) => (i + dir + count) % count);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    const threshold = 40;
    if (Math.abs(delta) > threshold) {
      if (delta < 0) go(+1);
      else go(-1);
    }
    touchStartX.current = null;
  };

  const currentProduct = products[idx];
  const productName = currentProduct?.name?.en || currentProduct?.name || "";

  return (
    <div className="category-tile" role="group" aria-label={title}>
      <div
        className="category-tile__media"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {displayedUrl ? (
          <img
            key={displayedUrl} // helps CSS fade on src change
            src={displayedUrl}
            alt={productName || title}
            loading="lazy"
            className={cn("category-tile__img", isLoadingNext && "is-loading-next")}
          />
        ) : (
          <div className="category-tile__fallback">
            <span>No image</span>
          </div>
        )}

        {/* Optional tiny spinner over the image while the next one preloads */}
        {isLoadingNext && (
          <div className="category-tile__spinner" aria-hidden>
            <div className="spinner-dot" />
          </div>
        )}

        <div className="category-tile__overlay" />

        <div className="category-tile__text">
          <h3 className="category-tile__title">{title}</h3>
          {productName ? <p className="category-tile__product">{productName}</p> : null}
          {cat?.description?.en ? <p className="category-tile__desc">{cat.description.en}</p> : null}
        </div>

        {count > 1 && (
          <>
            <button
              type="button"
              className="category-tile__nav category-tile__nav--prev"
              aria-label="Previous product"
              onClick={() => go(-1)}
            >
              <span aria-hidden>‹</span>
            </button>
            <button
              type="button"
              className="category-tile__nav category-tile__nav--next"
              aria-label="Next product"
              onClick={() => go(+1)}
            >
              <span aria-hidden>›</span>
            </button>
          </>
        )}
      </div>

      {count > 1 && (
        <div className="category-tile__dots" role="tablist" aria-label="Products pagination">
          {products.map((_, i) => (
            <button
              key={i}
              type="button"
              className={cn("category-tile__dot", i === idx && "is-active")}
              onClick={() => setIdx(i)}
              role="tab"
              aria-selected={i === idx}
              aria-label={`Go to product ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryGrid({
  categories = [],
  className,
}) {
  return (
    <div className={cn("category-grid", className)}>
      {categories
        .filter((cat) => {
          if (cat?.tag && String(cat.tag).indexOf('hide') !== -1) return false;
          // conso
          // if (['beverages','drink','nigiri', 'salad'].indexOf(String(cat?.name?.en || '').toLowerCase()) === -1) return false;
          return true;
        })
        .filter((cat) => {
          
          const products = Array.isArray(cat?.products) ? cat.products : [];
          // console.log('CategoryGrid filtering category', cat?.name?.en, 'with',products.some((product) => product?.photo), 'prophotosducts');
          return products.some((product) => product?.photo);
        })
        .map((cat) => (
          <CategoryTile key={String(cat.id)} cat={cat} />
        ))}
    </div>
  );
}

export default CategoryGrid;
